import { LinearProgress } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { convertStatusUrl } from '../../../../api/enumerations';
import { WorkOrderData } from '../../../../api/workOrders/types';
import { Card } from '../../Column/Card';
import { QuickFilters, QuickFilterKey } from '../TableView/QuickFilters';
import {
  CardSlot,
  EmptyBox,
  GalleryGrid,
  GalleryWrapper,
  SortMenuItem,
  SortSelect,
  StickyBar,
} from './styles';

interface GalleryViewProps {
  data: WorkOrderData[];
  loading: boolean;
}

type SortKey =
  | 'limit_finish_asc'
  | 'limit_finish_desc'
  | 'schedule_asc'
  | 'reference_desc'
  | 'client_asc';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'limit_finish_asc', label: 'limite — mais próximo' },
  { value: 'limit_finish_desc', label: 'limite — mais distante' },
  { value: 'schedule_asc', label: 'agendamento — mais próximo' },
  { value: 'reference_desc', label: 'OS — mais recente' },
  { value: 'client_asc', label: 'cliente — A → Z' },
];

const compareBy =
  (key: SortKey) =>
  (a: WorkOrderData, b: WorkOrderData): number => {
    const safe = (s?: string | null): number =>
      s ? dayjs(s).valueOf() : Number.MAX_SAFE_INTEGER;
    switch (key) {
      case 'limit_finish_asc':
        return safe(a.limit_finish_at) - safe(b.limit_finish_at);
      case 'limit_finish_desc':
        return safe(b.limit_finish_at) - safe(a.limit_finish_at);
      case 'schedule_asc':
        return (
          safe(a.inspection?.schedule_at) - safe(b.inspection?.schedule_at)
        );
      case 'reference_desc':
        return b.reference_number - a.reference_number;
      case 'client_asc':
        return (a.client_name || '').localeCompare(b.client_name || '');
      default:
        return 0;
    }
  };

const matchesQuickFilter = (
  row: WorkOrderData,
  filter: QuickFilterKey | null
): boolean => {
  if (!filter) return true;
  switch (filter) {
    case 'overdue':
      return row.sla_status === 'overdue';
    case 'near_deadline':
      return row.sla_status === 'near_deadline';
    case 'no_inspector':
      return !row.inspection?.engineer_name;
    case 'scheduled_today': {
      const date = row.inspection?.schedule_at;
      if (!date) return false;
      return dayjs(date).isSame(dayjs(), 'day');
    }
    case 'rejected_by_bank':
      return row.hive_request?.acceptance_status === 'rejected';
    default:
      return true;
  }
};

export function GalleryView({ data, loading }: GalleryViewProps): JSX.Element {
  const [sortKey, setSortKey] = useState<SortKey>('limit_finish_asc');
  const [quickFilter, setQuickFilter] = useState<QuickFilterKey | null>(null);
  const [visibleLimit, setVisibleLimit] = useState(60);

  const filtered = useMemo(
    () => data.filter((row) => matchesQuickFilter(row, quickFilter)),
    [data, quickFilter]
  );

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort(compareBy(sortKey));
    return copy;
  }, [filtered, sortKey]);

  const visible = sorted.slice(0, visibleLimit);

  return (
    <GalleryWrapper>
      <StickyBar>
        <QuickFilters active={quickFilter} onChange={setQuickFilter} />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#555' }}>
            {loading
              ? 'carregando...'
              : `${sorted.length} OS${quickFilter ? ' (filtradas)' : ''}`}
          </span>
          <SortSelect
            size="small"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
          >
            {SORT_OPTIONS.map((opt) => (
              <SortMenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </SortMenuItem>
            ))}
          </SortSelect>
        </div>
      </StickyBar>
      {loading && <LinearProgress />}
      {!loading && sorted.length === 0 && (
        <EmptyBox>
          <p>nenhuma OS encontrada com os filtros aplicados</p>
        </EmptyBox>
      )}
      <GalleryGrid>
        {visible.map((row) => (
          <CardSlot key={row.id}>
            <Link
              to={`/home/property/${row.id}/${convertStatusUrl(row.status)}`}
            >
              <Card cardData={row} />
            </Link>
          </CardSlot>
        ))}
      </GalleryGrid>
      {visible.length < sorted.length && (
        <EmptyBox>
          <button
            type="button"
            onClick={() => setVisibleLimit((v) => v + 60)}
            style={{ padding: '8px 16px' }}
          >
            carregar mais ({sorted.length - visible.length} restantes)
          </button>
        </EmptyBox>
      )}
    </GalleryWrapper>
  );
}
